import { notNull } from './Types';

import { isArray } from '@easterngraphics/wcf/modules/utils/array';
import { isBoolean } from '@easterngraphics/wcf/modules/utils/boolean';
import { isNumber } from '@easterngraphics/wcf/modules/utils/number';
import { isString } from '@easterngraphics/wcf/modules/utils/string';

/** last updated for eaiws 4.12 */
export interface EaiwsGeometryExportSetting {
    'preview.enabled'?: boolean;

    format: '3DS' | 'DWG' | 'GFX' | 'SKP' | 'FBX' | 'OBJ' | 'DAE' | 'IGXC' | 'GFJ' | 'GLTF' | 'USD' | 'PEC' | 'L3D' | 'GLDF';
    contentDisposition?: string;
    filename?: string;
    /** default false */
    hideSubArticles?: boolean;
    /** default false */
    centerXZ?: boolean;
    layerControl?: string;

    /** only DWG default true */
    use3DProxies?: boolean;
    /** only DWG default false */
    acadColorIndex?: boolean;
    /** only DWG default false */
    no3D?: boolean;
    /** only DWG default false */
    dxf?: boolean;
    /** only DWG default true */
    r17?: boolean;
    /** only DWG default false */
    annotation?: boolean;
    /** only DWG default false */
    edwg?: boolean;
    /** only DWG (experimental) */
    layerRules?: string;

    /** only GFX default false */
    revit?: boolean;
    /** only GFX default false */
    'obx.enabled'?: boolean;
    /** only GFX default 'UTF-8' */
    'obx.encoding'?: string;

    /** only SKP */
    skpVersion?: 'SU3' | 'SU4' | 'SU5' | 'SU6' | 'SU7' | 'SU8' | 'SU2013' | 'SU2014' | 'SU2015' | 'SU2016';

    /** only DAE default false */
    edges?: boolean;

    /** only IGXC default false */
    ofmlMaterials?: boolean;
    /** only IGXC default false */
    basketIds?: boolean;
    /** only IGXC default false */
    cc128?: boolean;
    /** only IGXC default false */
    jsonArrays?: boolean;
    /** only IGXC default false */
    /** only IGXC default false */
    layers?: boolean;

    /** only GFX, 3DS, OBJ, DAE default false */
    compression?: boolean;

    /** only SKP, FBX, DAE */
    resolution?: 'Default' | 'Low';

    /** only 3DS, DWG, SKP, FBX, OBJ, DAE default 1.0 */
    scale?: number;

    /** only GLTF, USD */
    ascii?: boolean;
    /** only GLTF, USD */
    texTrans?: boolean;

    /** only GFJ default false */
    dwgSymbol?: boolean;

    /** only GFX, IGXC, GFJ */
    serverBase?: string;
    /** only GFX, IGXC, GFJ (rfa is not part of eaiws spec) */
    geometryExtensions?: Array<'.geo' | '.3ds' | '.dxf' | '.dwg' | '.obj' | '.glb' | '.gltf' | '.rfa'>;
    /** only GFX, IGXC, GFJ */
    imageExtensions?: Array<'.rgb' | '.tga' | '.jpg' | '.bmp'>;

    /**
     * GFX: Hierarchy, Flat, MatrixStack, MaterialStack supported
     * FBX: Hierarchy, Flat supported
     * IGXC: Path, Children supported
     */
    hierarchyMode?: 'Hierarchy' | 'MatrixStack' | 'MaterialStack' | 'Flat' | 'Path' | 'Children';

    /** only 3DS, FBX, DAE default false*/
    duplicateFaces?: boolean;

    /** only 3DS default true */
    empty?: boolean;

    /** only 3DS, DWG, SKP, FBX, OBJ, DAE
     *
     * default for 3DS, SKP, FBX, OBJ, DAE: false
     *
     * default for DWG: true
     *
     * if materials=false or textureToColor=true or acadColorIndex=true the default value is always false
     */
    textures?: boolean;

    /**
     * only DWG, SKP, FBX, OBJ, DAE, IGXC
     *
     * default for DWG, SKP, FBX, DAE: true
     *
     * default for OBJ, IGXC: false
     *
     */
    materials?: boolean;

    /** only DWG, SKP, FBX, DAE default false */
    textureToColor?: boolean;

    /** onlx FBX defaulf false */
    staticMaterials?: boolean;
    /** only FBX default false */
    shortMaterialNames?: boolean;

    /**
     * only DWG, SKP, FBX, DAE
     *
     * default for SKP, FBX, DAE: true
     *
     * default for DWG: false
     */
    no2D?: boolean;

    /** only GFJ, IGXC */
    maxImageSize?: number;

    /**
     * available for PEC and DWG
     * default: false
     */
    omitPriceData?: boolean;

    /** available for PEC */
    'dwg.enabled'?: boolean;
}

export function EaiwsGeometryExportSettingToOption(setting: EaiwsGeometryExportSetting): Array<string> {
    return Object.keys(setting).map((key) => {
        const value: string | boolean | number | undefined = setting[key];

        if (isArray(value)) {
            return `${key}=${value.join(',')}`;
        }

        if (isString(value) || isNumber(value)) {
            return `${key}=${value}`;
        }

        if (isBoolean(value)) {
            return `${key}=${value ? 'true' : 'false'}`;
        }

        return null;
   }).filter(notNull);
}